import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Alert, Button, Card, Input, Result, Segmented, Skeleton, Typography } from "antd";
import { FilePdfOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { decidePublic, getPublicChangeOrder, publicPdfUrl, publicPhotoUrl } from "./api";
import { formatDateTime } from "../workReports/format";
import { SignatureForm } from "../workReports/SignDialog";
import ChangeOrderPreview from "./ChangeOrderPreview";
import "../workReports/workReports.css";
import "./changeOrders.css";

/** The customer's page behind the approval link: read, then approve with a signature or reject. */
const PublicChangeOrder = () => {
  const { t, i18n } = useTranslation("dashboard");
  const { token } = useParams();
  const domain = new URLSearchParams(useLocation().search).get("domain") || "";
  const [state, setState] = useState({ loading: true });
  const [choice, setChoice] = useState("approved");
  const [rejectName, setRejectName] = useState("");
  const [rejectNote, setRejectNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getPublicChangeOrder(token, domain)
      .then(res => setState({ loading: false, ...res.data }))
      .catch(() => setState({ loading: false, failed: true }));
  }, [token, domain]);

  const send = async body => {
    setBusy(true);
    setError("");
    try {
      const res = await decidePublic(token, domain, body);
      setState(prev => ({
        ...prev,
        data: res.data.data,
        canDecide: false,
        justDecided: body.status
      }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(
        err?.response?.data?.errorMessage || t("Your answer could not be saved. Please try again.")
      );
    } finally {
      setBusy(false);
    }
  };

  const view = state.data;

  return (
    <div className="change-orders-page co-public" dir={i18n.dir?.()}>
      <div className="co-public-inner">
        {state.loading ? (
          <Card>
            <Skeleton active paragraph={{ rows: 10 }} />
          </Card>
        ) : state.failed ? (
          <Card>
            <Result
              status="warning"
              title={t("This link is not valid or has expired.")}
              subTitle={t("Please ask the company for a new link.")}
            />
          </Card>
        ) : (
          <>
            {view.seller?.name && (
              <div className="co-public-company">
                {view.seller.logo ? <img src={view.seller.logo} alt="" /> : null}
                <Typography.Text strong>{view.seller.name}</Typography.Text>
              </div>
            )}
            {state.justDecided && (
              <Result
                status={state.justDecided === "approved" ? "success" : "info"}
                title={
                  state.justDecided === "approved"
                    ? t("Thank you, the change order is approved.")
                    : t("Thank you, we have received your rejection.")
                }
                extra={
                  <Button
                    icon={<FilePdfOutlined />}
                    href={publicPdfUrl(token, domain)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("Download PDF")}
                  </Button>
                }
              />
            )}
            {!state.canDecide && !state.justDecided && (
              <Alert
                className="mb-3"
                type="info"
                showIcon
                message={t("This change order has already been decided.")}
                action={
                  <Button
                    size="small"
                    href={publicPdfUrl(token, domain)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    PDF
                  </Button>
                }
              />
            )}
            <Card className="mb-3">
              <ChangeOrderPreview
                view={view}
                photoSrc={photoId => publicPhotoUrl(token, domain, photoId)}
              />
            </Card>
            {state.canDecide && (
              <Card className="mb-3" title={t("Your decision")}>
                <Segmented
                  block
                  value={choice}
                  onChange={setChoice}
                  options={[
                    { value: "approved", label: t("Approve") },
                    { value: "rejected", label: t("Reject") }
                  ]}
                  className="mb-3"
                />
                {choice === "approved" ? (
                  <>
                    <Typography.Paragraph type="secondary">
                      {t("With your signature you order this change at the price shown.")}
                    </Typography.Paragraph>
                    <div className="work-reports-page">
                      <SignatureForm
                        defaultName={view.customer?.contactName || view.customer?.name || ""}
                        showCopy={false}
                        showLocation={false}
                        submitting={busy}
                        onSubmit={values =>
                          send({ status: "approved", name: values.name, image: values.image })
                        }
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <Input
                      className="mb-2"
                      size="large"
                      value={rejectName}
                      maxLength={120}
                      placeholder={t("Your name")}
                      onChange={e => setRejectName(e.target.value)}
                    />
                    <Input.TextArea
                      className="mb-2"
                      value={rejectNote}
                      maxLength={2000}
                      autoSize={{ minRows: 3 }}
                      placeholder={t("Why do you reject it?")}
                      onChange={e => setRejectNote(e.target.value)}
                    />
                    <Button
                      danger
                      size="large"
                      block
                      loading={busy}
                      onClick={() =>
                        send({ status: "rejected", name: rejectName, note: rejectNote })
                      }
                    >
                      {t("Reject change order")}
                    </Button>
                  </>
                )}
                {error && <Alert className="mt-3" type="error" showIcon message={error} />}
                {state.expiresAt && (
                  <Typography.Paragraph type="secondary" className="mt-3">
                    {t("Valid until {{date}}", {
                      date: formatDateTime(state.expiresAt, i18n.language)
                    })}
                  </Typography.Paragraph>
                )}
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PublicChangeOrder;
